import React, { useEffect, useState } from "react";
import {
  Button,
  ConfigProvider,
  Form,
  Modal,
  Select,
  Spin,
  message,
} from "antd";
import style from "css/admin/component.module.css";
import SearchInput from "component/admin/search/searchInput";
import axios from "axios";

function MemberManualRegisterModal({ open, onCancel, onSuccess }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [findLoading, setFindLoading] = useState(false);
  const [memberFound, setMemberFound] = useState(false);
  const [duplicateChecked, setDuplicateChecked] = useState(false);

  // 셀렉트 옵션
  const [campusOptions, setCampusOptions] = useState([]);
  const [memberTypeOptions, setMemberTypeOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    setMemberFound(false);

    // 캠퍼스 (CD06)
    axios
      .get("/code", { params: { GROUP_CD: "CD06" } })
      .then((res) => {
        if (res.status === 200 && Array.isArray(res.data)) {
          setCampusOptions(
            res.data.map((c) => ({ value: c.CODE_CD, label: c.VIEW_NM }))
          );
        }
      })
      .catch(() => setCampusOptions([]));

    // 신분 (CD01)
    axios
      .get("/code", { params: { GROUP_CD: "CD01" } })
      .then((res) => {
        if (res.status === 200 && Array.isArray(res.data)) {
          setMemberTypeOptions(
            res.data.map((c) => ({ value: c.CODE_CD, label: c.VIEW_NM }))
          );
        }
      })
      .catch(() => setMemberTypeOptions([]));

    // 재학/재직 상태 (CD04만, CD05 퇴학/퇴직 제외)
    axios
      .get("/code", { params: { GROUP_CD: "CD04" } })
      .then((res) => {
        if (res.status === 200 && Array.isArray(res.data)) {
          setStatusOptions(
            res.data.map((c) => ({
              value: c.CODE_CD,
              label: c.VIEW_NM,
            }))
          );
        }
      })
      .catch(() => setStatusOptions([]));
  }, [open, form]);

  const closeHandler = () => {
    form.resetFields();
    setMemberFound(false);
    setDuplicateChecked(false);
    onCancel();
  };

  // 학번 조회 → 이미 등록된 사용자인지 확인
  const handleFind = async () => {
    const memberId = form.getFieldValue("memberId");
    if (!memberId || !memberId.trim()) {
      message.warning("학번/사번을 입력하세요.");
      return;
    }
    setFindLoading(true);
    try {
      const res = await axios.get("/user/find", {
        params: { MEMBER_ID: memberId.trim() },
      });
      if (res.status === 200 && res.data) {
        setMemberFound(true);
        setDuplicateChecked(true);
        message.warning("이미 등록된 사용자입니다.");
      } else {
        setMemberFound(false);
        setDuplicateChecked(true);
        message.success("등록 가능한 학번/사번입니다.");
      }
    } catch (e) {
      setMemberFound(false);
      setDuplicateChecked(true);
      message.success("등록 가능한 학번/사번입니다.");
    } finally {
      setFindLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (!duplicateChecked) {
        message.warning("중복확인을 먼저 진행해주세요.");
        return;
      }
      if (memberFound) {
        message.error("이미 등록된 사용자입니다.");
        return;
      }
      setLoading(true);
      const res = await axios.post("/user/manual", {
        MEMBER_ID: values.memberId.trim(),
        MEMBER_NM: values.memberNm.trim(),
        EMAIL: values.email || "",
        CAMPUS_CD: values.campusCd || "",
        MEMBER_TYPE: values.memberType || null,
        STATUS: values.status || "",
        DEPARTMENT_CD: values.departmentCd || "",
        DEPARTMENT_NM: values.departmentNm || "",
      });

      if (res.status === 200 && res.data) {
        const d = res.data;
        if (d.status === "FAIL") {
          message.error(d.message || "등록에 실패했습니다.");
        } else {
          message.success(d.message || "등록이 완료되었습니다.");
          if (typeof onSuccess === "function") onSuccess(d);
        }
      }
    } catch (e) {
      if (e?.errorFields) {
        message.error("필수 항목을 확인해주세요.");
        return;
      }
      const msg =
        e?.response?.data?.message ??
        e?.response?.data ??
        e?.message ??
        "등록 중 오류가 발생했습니다.";
      message.error(String(msg));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <span className={style.searchItem_label} style={{ fontSize: 18 }}>
          인사정보 수기등록
        </span>
      }
      open={open}
      onCancel={closeHandler}
      closable={false}
      maskClosable={!loading}
      footer={
        <ConfigProvider
          theme={{
            token: { colorPrimary: "#527ff2" },
            components: {
              Button: {
                colorPrimaryHover: "#4a72de",
                colorPrimaryActive: "#3f66c8",
              },
            },
          }}
        >
          <div className={style.modal_footer_wrap}>
            <Button
              key="cancel"
              onClick={closeHandler}
              disabled={loading}
              className={style.modal_footer_btn}
            >
              취소
            </Button>
            <Button
              key="submit"
              type="primary"
              onClick={handleSubmit}
              loading={loading}
              className={style.modal_footer_btn}
            >
              등록하기
            </Button>
          </div>
        </ConfigProvider>
      }
      rootClassName={style.manual_modal}
      styles={{ mask: { backdropFilter: "none" } }}
      width={480}
      centered
    >
      <Spin spinning={loading}>
        <Form form={form} requiredMark={false} style={{ marginTop: 10 }}>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 16,
              padding: "23px 0",
            }}
          >
            <div className={style.form_row}>
              <div className={style.form_label}>학번/사번</div>
              <Form.Item
                name="memberId"
                rules={[{ required: true, message: "학번/사번을 입력하세요" }]}
                style={{ flex: 1, marginBottom: 0 }}
                validateStatus={
                  duplicateChecked ? (memberFound ? "error" : "success") : undefined
                }
              >
                <SearchInput
                  placeholder="학번/사번을 입력하세요"
                  fullWidth
                  style={
                    duplicateChecked
                      ? { borderColor: memberFound ? "#ff4d4f" : "#52c41a" }
                      : undefined
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleFind();
                    }
                  }}
                  onChange={() => {
                    if (memberFound) setMemberFound(false);
                    if (duplicateChecked) setDuplicateChecked(false);
                  }}
                />
              </Form.Item>
              <Button
                onClick={handleFind}
                loading={findLoading}
                style={{
                  height: 36,
                  borderRadius: 5,
                  fontSize: 14,
                  fontFamily: "Pretendard",
                  flexShrink: 0,
                }}
              >
                중복확인
              </Button>
            </div>
            <div className={style.form_row}>
              <div className={style.form_label}>성명</div>
              <Form.Item
                name="memberNm"
                rules={[{ required: true, message: "성명을 입력하세요" }]}
                style={{ flex: 1, marginBottom: 0 }}
              >
                <SearchInput placeholder="성명을 입력하세요" fullWidth />
              </Form.Item>
            </div>
            <div className={style.form_row}>
              <div className={style.form_label}>이메일</div>
              <Form.Item name="email" style={{ flex: 1, marginBottom: 0 }}>
                <SearchInput placeholder="" fullWidth />
              </Form.Item>
            </div>
            <div className={style.form_row}>
              <div className={style.form_label}>캠퍼스</div>
              <Form.Item name="campusCd" style={{ flex: 1, marginBottom: 0 }}>
                <Select
                  className={style.search_cascader}
                  placeholder="선택하세요"
                  allowClear
                  options={campusOptions}
                />
              </Form.Item>
            </div>
            <div className={style.form_row}>
              <div className={style.form_label}>구분</div>
              <Form.Item
                name="memberType"
                style={{ flex: 1, marginBottom: 0 }}
              >
                <Select
                  className={style.search_cascader}
                  placeholder="선택하세요"
                  allowClear
                  options={memberTypeOptions}
                />
              </Form.Item>
            </div>
            <div className={style.form_row}>
              <div className={style.form_label}>재학/재직 상태</div>
              <Form.Item name="status" style={{ flex: 1, marginBottom: 0 }}>
                <Select
                  className={style.search_cascader}
                  placeholder="선택하세요"
                  allowClear
                  options={statusOptions}
                />
              </Form.Item>
            </div>
            <div className={style.form_row}>
              <div className={style.form_label}>학과코드</div>
              <Form.Item
                name="departmentCd"
                style={{ flex: 1, marginBottom: 0 }}
              >
                <SearchInput placeholder="학과코드 (선택)" fullWidth />
              </Form.Item>
            </div>
            <div className={style.form_row}>
              <div className={style.form_label}>학과명</div>
              <Form.Item
                name="departmentNm"
                style={{ flex: 1, marginBottom: 0 }}
              >
                <SearchInput placeholder="학과명 (선택)" fullWidth />
              </Form.Item>
            </div>
          </div>
        </Form>
      </Spin>
    </Modal>
  );
}

export default MemberManualRegisterModal;
